import { UserName } from "@/components/UserName";
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { LoadMore } from "@/components/LoadMore";
import { usePaged } from "@/hooks/usePaged";
import { formatDateTime } from "@/lib/datetime";

export default function LoginHistoryPage() {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [result, setResult] = useState("");

  useEffect(() => {
    api.get("/admin/login-history", { params: email ? { email } : {} }).then(({ data }) => setRows(data));
  }, [email]);

  // build the user filter list from whatever we've loaded (plus keep current selection)
  const emails = useMemo(() => {
    const s = new Set(rows.map((r) => r.email).filter(Boolean));
    if (email) s.add(email);
    return Array.from(s).sort();
  }, [rows, email]);

  const filtered = useMemo(() => {
    if (!result) return rows;
    const want = result === "success";
    return rows.filter((r) => !!r.success === want);
  }, [rows, result]);

  const { visible, hasMore, more, shownCount, total } = usePaged(filtered, 50);

  return (
    <div data-testid="login-history-page">
      <h1 className="font-display text-2xl font-bold tracking-tight">Login History</h1>
      <p className="text-sm text-slate-500 mb-4">Recent sign-ins — device, time, and outcome. Filter by user to eyeball one account.</p>

      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-wider text-slate-500">User</label>
          <select data-testid="login-history-user-filter" value={email} onChange={(e) => setEmail(e.target.value)}
            className="rounded-lg border border-slate-200 text-sm px-2 py-1.5 bg-white">
            <option value="">All users</option>
            {emails.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
          {email && <button data-testid="login-history-clear" onClick={() => setEmail("")} className="text-xs underline text-slate-500">clear</button>}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-wider text-slate-500">Result</label>
          <select data-testid="login-history-result-filter" value={result} onChange={(e) => setResult(e.target.value)}
            className="rounded-lg border border-slate-200 text-sm px-2 py-1.5 bg-white">
            <option value="">All</option>
            <option value="success">Success</option>
            <option value="fail">Fail</option>
          </select>
        </div>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto rounded-lg">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="bg-slate-100 text-left uppercase tracking-wider text-[10px] text-slate-500">
              <th className="py-2 px-3 border-r border-slate-200">Time (yard)</th>
              <th className="py-2 px-3 border-r border-slate-200">Email</th>
              <th className="py-2 px-3 border-r border-slate-200">Result</th>
              <th className="py-2 px-3 border-r border-slate-200">Device</th>
              <th className="py-2 px-3 border-r border-slate-200">Reason</th>
              <th className="py-2 px-3 border-r border-slate-200">IP</th>
              <th className="py-2 px-3">User agent</th>
            </tr>
          </thead>
          <tbody data-testid="login-history-body">
            {visible.map((r) => (
              <tr key={r.id} className="log-row border-t border-slate-200">
                <td className="py-1 px-3 border-r border-slate-100 whitespace-nowrap">{formatDateTime(r.timestamp)}</td>
                <td className="py-1 px-3 border-r border-slate-100"><UserName email={r.email} inline /></td>
                <td className="py-1 px-3 border-r border-slate-100">
                  {r.success ? <span className="text-green-600">SUCCESS</span> : <span className="text-destructive">FAIL</span>}
                </td>
                <td className="py-1 px-3 border-r border-slate-100 text-slate-700">{r.device || "—"}</td>
                <td className="py-1 px-3 border-r border-slate-100 text-slate-500">{r.reason}</td>
                <td className="py-1 px-3 border-r border-slate-100">{r.ip}</td>
                <td className="py-1 px-3 max-w-xs truncate text-slate-400" title={r.user_agent}>{r.user_agent}</td>
              </tr>
            ))}
            {total === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-400">No sign-ins recorded.</td></tr>}
          </tbody>
        </table>
      </div>

      <LoadMore shownCount={shownCount} total={total} hasMore={hasMore} onMore={more} noun="sign-in" testid="login-history-load-more" />
    </div>
  );
}
